import { Plus, Trash2 } from 'lucide-react';
import type { ProblemExample } from '@/lib/api/problems';
import { Button } from '@/components/ui/button';
import { RichTextEditor } from '@/components/RichTextEditor';
import { constraintsToHtml, htmlToConstraints } from '@/lib/constraintsHtml';

interface Props {
  title: string;
  description: string;
  constraints: string;
  examples: ProblemExample[];
  followUp: string;
  onChange: (field: string, value: unknown) => void;
}

const EMPTY_EXAMPLE: ProblemExample = { input: '', output: '', explanation: '' };

export function ProblemDescriptionTab({
  title,
  description,
  constraints,
  examples,
  followUp,
  onChange,
}: Props) {
  function updateExample(i: number, field: keyof ProblemExample, value: string) {
    onChange('examples', examples.map((ex, idx) => (idx === i ? { ...ex, [field]: value } : ex)));
  }

  function addExample() {
    onChange('examples', [...examples, { ...EMPTY_EXAMPLE }]);
  }

  function removeExample(i: number) {
    onChange('examples', examples.filter((_, idx) => idx !== i));
  }

  return (
    <div className="flex flex-col gap-5 p-4">
      {/* Title */}
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
          Title *
        </label>
        <input
          type="text"
          value={title}
          onChange={e => onChange('title', e.target.value)}
          placeholder="e.g. Two Sum"
          className="w-full border rounded-md px-3 py-2 text-[14px] font-semibold bg-white focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      {/* Problem Statement — maps 1:1 to content.statement (a single HTML blob). */}
      <div className="flex-1">
        <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
          Problem Statement
        </label>
        <RichTextEditor
          value={description}
          onChange={html => onChange('description', html)}
          placeholder="Describe the problem…"
        />
      </div>

      {/* Examples — this is what students actually see on the problem page. */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Examples
          </label>
          <Button size="sm" variant="outline" className="h-7 text-[12px] gap-1" onClick={addExample}>
            <Plus className="h-3.5 w-3.5" />
            Add Example
          </Button>
        </div>
        {examples.length === 0 && (
          <p className="text-[13px] text-muted-foreground border rounded-md px-3 py-4 text-center bg-muted/30">
            No examples yet. Students won&apos;t see an Example section until you add one.
          </p>
        )}
        <div className="flex flex-col gap-3">
          {examples.map((ex, i) => (
            <div key={i} className="border rounded-md p-3 bg-muted/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[12px] font-semibold">Example {i + 1}</span>
                <button
                  onClick={() => removeExample(i)}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  aria-label={`Remove example ${i + 1}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="flex flex-col gap-2">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    Input
                  </label>
                  {/* Textareas, not inputs: stdio problems (e.g. pattern printing)
                      have multi-line, whitespace-significant I/O. */}
                  <textarea
                    value={ex.input}
                    onChange={e => updateExample(i, 'input', e.target.value)}
                    placeholder='nums = [2,7,11,15], target = 9'
                    rows={Math.max(1, ex.input.split('\n').length)}
                    spellCheck={false}
                    className="w-full border rounded-md px-2.5 py-1.5 text-[13px] font-mono bg-white resize-vertical whitespace-pre focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    Output
                  </label>
                  <textarea
                    value={ex.output}
                    onChange={e => updateExample(i, 'output', e.target.value)}
                    placeholder="[0,1]"
                    rows={Math.max(1, ex.output.split('\n').length)}
                    spellCheck={false}
                    className="w-full border rounded-md px-2.5 py-1.5 text-[13px] font-mono bg-white resize-vertical whitespace-pre focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    Explanation (optional)
                  </label>
                  <input
                    type="text"
                    value={ex.explanation ?? ''}
                    onChange={e => updateExample(i, 'explanation', e.target.value)}
                    placeholder="Because nums[0] + nums[1] == 9, we return [0, 1]."
                    className="w-full border rounded-md px-2.5 py-1.5 text-[13px] font-mono bg-white focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Constraints — edited as a bullet list; each <li> becomes one
          content.constraints[] entry (see constraintsToHtml/htmlToConstraints). */}
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
          Constraints
        </label>
        <RichTextEditor
          value={constraintsToHtml(constraints)}
          onChange={html => onChange('constraints', htmlToConstraints(html))}
          placeholder="2 ≤ nums.length ≤ 10⁴ — add each constraint as a bullet"
        />
        <p className="mt-1 text-[11px] text-muted-foreground">Each bullet becomes one constraint line.</p>
      </div>

      {/* Follow-up */}
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
          Follow-up (optional)
        </label>
        <textarea
          value={followUp}
          onChange={e => onChange('follow_up', e.target.value)}
          placeholder="Can you come up with an algorithm that is less than O(n^2) time complexity?"
          rows={2}
          className="w-full border rounded-md px-3 py-2 text-[13px] font-mono bg-white resize-vertical focus:outline-none focus:ring-1 focus:ring-ring leading-relaxed"
        />
      </div>
    </div>
  );
}
